import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { DateRange } from "@angular/material/datepicker";
import { TIME_DIRECTION_AGO } from "@bitwarden/web-vault/app/components/date-picker/calendar/calendar.component";

type PickerType = "start" | "end";

@Component({
  selector: "app-file-details-stage",
  templateUrl: "./file-details-stage.component.html",
})
export class FileDetailsStageComponent implements OnInit, OnChanges {
  @Input() uploadedFileName: string;
  @Input() statementFromDate: string;
  @Input() statementToDate: string;
  @Output() fileNameChange = new EventEmitter<string>();
  @Output() statementDatesSelected = new EventEmitter<{ fromDate: string; toDate: string }>();

  statementPeriodForm: FormGroup;
  originalDateRange: DateRange<Date>;
  showStartPicker = false;
  showEndPicker = false;

  // Snapshotted when a picker opens so the bound references stay stable while it's
  // open (a getter returning a fresh object each CD would clobber the live selection).
  calendarDateRange: DateRange<Date>;
  startDateFilter: (date: Date) => boolean = () => true;
  endDateFilter: (date: Date) => boolean = () => true;

  // CDK overlay: open below the trigger, flip above if there isn't room.
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 8 },
    { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -8 },
  ];
  protected readonly AGO = TIME_DIRECTION_AGO;
  private readonly today: Date;
  private defaultFromDate: string;
  private defaultToDate: string;

  constructor(private formBuilder: FormBuilder) {
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);

    // Set default dates to last month
    this.setDefaultDateRange();
    this.originalDateRange = new DateRange(
      this.parseLocalDate(this.defaultFromDate),
      this.parseLocalDate(this.defaultToDate),
    );
    this.calendarDateRange = this.originalDateRange;

    this.statementPeriodForm = this.formBuilder.group({
      fileName: ["", Validators.required],
      startDate: [this.defaultFromDate, Validators.required],
      endDate: [this.defaultToDate, Validators.required],
    });
  }

  @HostListener("window:keydown.escape")
  onEscapeKey(): void {
    this.closePickers();
  }

  private setDefaultDateRange(): void {
    const today = new Date();
    const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

    this.defaultFromDate = this.formatDate(firstDayOfLastMonth);
    this.defaultToDate = this.formatDate(lastDayOfLastMonth);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Parse a "YYYY-MM-DD" string as a *local* date. `new Date("YYYY-MM-DD")` parses as UTC
   * midnight, which renders as the previous day in negative-UTC timezones — so the statement
   * period could silently shift by a day. Building from parts keeps it in local time.
   */
  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  ngOnInit(): void {
    this.updateFileNameInForm();

    // Use Promise.resolve to emit on next tick, avoiding ExpressionChangedAfterItHasBeenCheckedError
    Promise.resolve().then(() => {
      const startDate = this.statementPeriodForm.value.startDate;
      const endDate = this.statementPeriodForm.value.endDate;
      if (startDate && endDate) {
        this.statementDatesSelected.emit({ fromDate: startDate, toDate: endDate });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["uploadedFileName"] && !changes["uploadedFileName"].firstChange) {
      this.updateFileNameInForm();
    }
  }

  private updateFileNameInForm(): void {
    if (this.uploadedFileName) {
      this.statementPeriodForm.patchValue({
        fileName: this.uploadedFileName,
      });
    }

    // Also update dates if they exist
    if (this.statementFromDate) {
      this.statementPeriodForm.patchValue({
        startDate: this.statementFromDate,
      });
    }

    if (this.statementToDate) {
      this.statementPeriodForm.patchValue({
        endDate: this.statementToDate,
      });
    }
  }

  get startDateText(): string {
    return this.statementPeriodForm.value.startDate || "";
  }

  get endDateText(): string {
    return this.statementPeriodForm.value.endDate || "";
  }

  showError(controlName: "startDate" | "endDate"): boolean {
    const control = this.statementPeriodForm.controls[controlName];
    return control.invalid && control.dirty;
  }

  togglePicker(type: PickerType): void {
    const willOpen = type === "start" ? !this.showStartPicker : !this.showEndPicker;
    this.showStartPicker = type === "start" && willOpen;
    this.showEndPicker = type === "end" && willOpen;
    if (willOpen) {
      this.snapshotCalendarInputs();
    }
  }

  /**
   * Freeze the calendar's range + date filters at open time. Start can't be after the
   * chosen end (or in the future); end can't be before the chosen start (or in the future).
   */
  private snapshotCalendarInputs(): void {
    const { startDate, endDate } = this.statementPeriodForm.value;
    const start = startDate ? this.parseLocalDate(startDate) : null;
    const end = endDate ? this.parseLocalDate(endDate) : null;
    this.calendarDateRange = new DateRange(start, end);
    this.startDateFilter = (date: Date) => date <= this.today && (!end || date <= end);
    this.endDateFilter = (date: Date) => date <= this.today && (!start || date >= start);
  }

  closePickers(): void {
    this.showStartPicker = false;
    this.showEndPicker = false;
  }

  /** Fired when the calendar's Confirm button is pressed. */
  onCalendarRangeChange(range: DateRange<Date>): void {
    const startDate = range.start
      ? this.formatDate(range.start)
      : this.statementPeriodForm.value.startDate;
    const endDate = range.end ? this.formatDate(range.end) : this.statementPeriodForm.value.endDate;

    this.statementPeriodForm.patchValue({ startDate, endDate });
    this.statementPeriodForm.controls["startDate"].markAsDirty();
    this.statementPeriodForm.controls["endDate"].markAsDirty();
    this.closePickers();

    if (startDate && endDate) {
      this.statementDatesSelected.emit({ fromDate: startDate, toDate: endDate });
    }
  }

  onFileNameChange(newFileName: string): void {
    const fileName = this.statementPeriodForm.controls["fileName"];
    fileName.setValue(newFileName);
    fileName.markAsDirty();
    this.fileNameChange.emit(newFileName);
  }

  validateForm(): boolean {
    if (this.statementPeriodForm.valid) {
      return true;
    }

    // Mark all fields as touched/dirty to surface validation errors
    Object.values(this.statementPeriodForm.controls).forEach((control) => {
      control.markAsTouched();
      control.markAsDirty();
    });

    return false;
  }
}
